import type { Ari } from '@atlassian/ari';

import {
	type AudioFormat,
	type AudioRequest,
	type CustomLength,
	type CustomTone,
	getAudio,
} from '../../api/cc-smarts/getAudio';
import type { AudioFeatureType } from '../../api/cc-smarts/models/AudioFeature';
import { AudioStreamingState } from '../state/AudioPlayerStreamingStore';

import {
	appendBufferAsync,
	getBufferAheadSeconds,
	waitForBufferToDrain,
} from './AudioBufferEffects';
import { getConfluenceContentAri } from './getConfluenceContentAri';

export type AudioPlayerStreamingAction =
	| { type: AudioStreamingState.UNINITIALIZED }
	| { type: AudioStreamingState.LOADING }
	| { type: AudioStreamingState.ERROR; error: Error }
	| { type: AudioStreamingState.STREAM_STARTED; response: ReadableStream<Uint8Array> }
	| { type: AudioStreamingState.STREAM_DISPOSED };

export type AudioPlayerRequestParams = {
	locale: string;
	contentId: string;
	contentType: string;
	type: AudioFeatureType;
	instructions: {
		tone: CustomTone;
		length: CustomLength;
	};
	format: AudioFormat;
	activationId: string;
	product: string;
	cloudId: string;
};

type AudioFetchQuery = (requestParams: AudioRequest) => Promise<ReadableStream<Uint8Array>>;

export class AudioPlayerStreamingManager {
	private response: ReadableStream<Uint8Array> | null = null;
	private validatedRequestParams: AudioRequest | null = null;
	private unvalidatedRequestParams: AudioPlayerRequestParams;
	private audioElement: HTMLAudioElement | null = null;
	private mediaSource: MediaSource | null = null;
	private isReadyToConsumeStream: boolean = false;

	private fetchAudioQuery: AudioFetchQuery;
	private onAudioStreamingChange: (action: AudioPlayerStreamingAction) => void;
	private getAri: (siteId: string, contentId: string, contentType: string) => Ari | undefined;
	private audioMimeType: string;
	private maxBufferAheadSeconds: number;
	private resumeBufferAheadSeconds: number;

	constructor(config: {
		requestParams: AudioPlayerRequestParams;
		onAudioStreamingChange: (action: AudioPlayerStreamingAction) => void;
		fetchAudioQuery?: AudioFetchQuery;
		getAri?: (siteId: string, contentId: string, contentType: string) => Ari | undefined;
		audioMimeType?: string;
		maxBufferAheadSeconds?: number;
		resumeBufferAheadSeconds?: number;
	}) {
		this.onAudioStreamingChange = config.onAudioStreamingChange;
		this.fetchAudioQuery = config.fetchAudioQuery ?? ((params) => getAudio(params));
		this.getAri = config.getAri ?? getConfluenceContentAri;
		this.audioMimeType = config.audioMimeType ?? 'audio/mpeg';
		this.maxBufferAheadSeconds = config.maxBufferAheadSeconds ?? 360;
		this.resumeBufferAheadSeconds = config.resumeBufferAheadSeconds ?? 180;
		this.unvalidatedRequestParams = config.requestParams;
	}

	public validateRequestParams() {
		const { locale, contentId, contentType, type, format, activationId, cloudId, instructions } =
			this.unvalidatedRequestParams;
		const ari = this.getAri(cloudId, contentId, contentType);
		if (!ari) {
			this.onAudioStreamingChange({
				type: AudioStreamingState.ERROR,
				error: new Error('Invalid content ARI'),
			});
			return;
		}

		this.validatedRequestParams = {
			ari,
			locale,
			type,
			format,
			activationId,
			instructions,
		};
	}

	public resetState() {
		this.onAudioStreamingChange({ type: AudioStreamingState.UNINITIALIZED });
	}

	async fetchAudio() {
		if (!this.validatedRequestParams) {
			return;
		}

		this.onAudioStreamingChange({ type: AudioStreamingState.LOADING });
		try {
			this.response = await this.fetchAudioQuery(this.validatedRequestParams);
			this.onAudioStreamingChange({
				type: AudioStreamingState.STREAM_STARTED,
				response: this.response,
			});
		} catch (error) {
			this.onAudioStreamingChange({
				type: AudioStreamingState.ERROR,
				error: error as Error,
			});
		}
	}

	streamAudioToMediaSource(audioElement: HTMLAudioElement | null) {
		if (!audioElement || !this.response) {
			return;
		}

		this.mediaSource = new MediaSource();
		this.audioElement = audioElement;
		this.isReadyToConsumeStream = true;

		const url = URL.createObjectURL(this.mediaSource);
		this.audioElement.src = url;
		this.audioElement.load();

		this.mediaSource.addEventListener('sourceopen', () => this.onMediaSourceOpen(url), {
			once: true,
		});
	}

	disposeStream() {
		this.isReadyToConsumeStream = false;
		if (this.mediaSource?.readyState === 'open') {
			try {
				this.mediaSource.endOfStream();
			} catch (e) {
				// Errors during cleanup can be safely ignored.
			}
		}
		this.audioElement = null;
		this.response = null;
		this.onAudioStreamingChange({ type: AudioStreamingState.STREAM_DISPOSED });
	}

	private onMediaSourceOpen = async (url: string) => {
		if (!this.mediaSource || !this.response || !this.audioElement) {
			return;
		}

		try {
			URL.revokeObjectURL(url);
			const sourceBuffer = this.mediaSource.addSourceBuffer(this.audioMimeType);
			sourceBuffer.mode = 'sequence';
			const reader = this.response.getReader();

			while (this.isReadyToConsumeStream) {
				// Backpressure: if bufferAhead is at or above the cap, wait for it to drain
				const bufferAheadSeconds = getBufferAheadSeconds(this.audioElement);
				if (bufferAheadSeconds >= this.maxBufferAheadSeconds) {
					await waitForBufferToDrain(
						this.audioElement,
						this.isReadyToConsumeStream,
						this.resumeBufferAheadSeconds,
					);
					if (!this.isReadyToConsumeStream) {
						break;
					}
				}
				const { done, value } = await reader.read();
				if (done) {
					break;
				}

				if (!this.isReadyToConsumeStream) {
					break;
				}
				// Ensure we have a proper BufferSource by copying to a new ArrayBuffer
				const buffer = new Uint8Array(value);
				await appendBufferAsync(sourceBuffer, buffer);
			}

			if (this.isReadyToConsumeStream) {
				this.disposeStream();
			}
		} catch (e) {
			this.onAudioStreamingChange({ type: AudioStreamingState.ERROR, error: e as Error });
		}
	};
}
